// Column definitions and static lookup data for the component-library
// version of Manage Expiring Leases (property-level). Kept free of hooks and
// components so every export here is plain, importable data.
import messages from './messages';

export const NAME = 'manageExpiringLeases';

// Six day-expiration buckets, boundaries ported from the legacy
// `useDateInfo` hook. `test` takes the days-until-lease-end number and
// returns whether that number falls in the bucket. The buckets partition
// every possible day count exactly once, including negative counts for a
// lease whose end date is already in the past (month-to-month).
export const EXPIRATION_BUCKETS = [
  {
    id: 'monthToMonth',
    label: messages.month.defaultMessage,
    test: (daysUntilLeaseEnd) => daysUntilLeaseEnd < 0,
  },
  {
    id: 'below30',
    label: `${messages.expiringText.defaultMessage} ${messages.under30.defaultMessage}`,
    test: (daysUntilLeaseEnd) =>
      daysUntilLeaseEnd >= 0 && daysUntilLeaseEnd <= 30,
  },
  {
    id: 'between30And60',
    label: `${messages.expiringText.defaultMessage} ${messages.between31and60.defaultMessage}`,
    test: (daysUntilLeaseEnd) =>
      daysUntilLeaseEnd > 30 && daysUntilLeaseEnd <= 60,
  },
  {
    id: 'between60And90',
    label: `${messages.expiringText.defaultMessage} ${messages.between61and90.defaultMessage}`,
    test: (daysUntilLeaseEnd) =>
      daysUntilLeaseEnd > 60 && daysUntilLeaseEnd <= 90,
  },
  {
    id: 'between90And120',
    label: `${messages.expiringText.defaultMessage} ${messages.between90and120.defaultMessage}`,
    test: (daysUntilLeaseEnd) =>
      daysUntilLeaseEnd > 90 && daysUntilLeaseEnd <= 120,
  },
  {
    id: 'over120',
    label: `${messages.expiringText.defaultMessage} ${messages.over120.defaultMessage}`,
    test: (daysUntilLeaseEnd) => daysUntilLeaseEnd > 120,
  },
];

// The default view hides this bucket until the user selects its card.
export const DEFAULT_HIDDEN_BUCKET_ID = 'over120';

// The four renewal statuses a lease can be filtered by. `name` is the
// machine token, `text` is the display label. Matches
// `Filter/filterConfigurations.js`'s `filterRenewalLeasesStatus`.
export const RENEWAL_STATUS_OPTIONS = [
  {
    name: 'completed',
    text: 'Completed',
  },
  {
    name: 'inProcess',
    text: 'In Process',
  },
  {
    name: 'incomplete',
    text: 'Not Started',
  },
  {
    name: 'onNotice',
    text: 'On Notice',
    textSecondary: 'On NTV',
  },
];

const YES_NO_FILTER_OPTIONS = [
  { text: 'Yes', value: 'yes' },
  { text: 'No', value: 'no' },
];

// Header definitions for the component-library `Table`. `isAllCommercial`
// swaps the resident column's label to the Tenant message, preserving the
// legacy Resident/Tenant label swap on all-commercial properties.
export const HEADERS = ({ isAllCommercial }) => [
  // Actions leads the row. This screen only renders while
  // `move-action-columns-to-left` is on, which is the same flag that moves the
  // column on every other table, so the order is declared here and
  // `useActionsFirstColumnOrder` has nothing to move.
  {
    id: 'actions',
    label: messages.actions.defaultMessage,
    sortable: false,
    searchable: false,
  },
  {
    id: 'leaseRenewalStatus',
    label: messages.leaseRenewalStatus.defaultMessage,
    sortable: true,
    searchable: true,
    multiselect: true,
    filterOptions: RENEWAL_STATUS_OPTIONS.map((status) => ({
      text: status.text,
      value: status.name,
    })),
    dataType: 'string',
  },
  {
    id: 'unit',
    label: messages.unit.defaultMessage,
    sortable: true,
    searchable: true,
    dataType: 'string',
  },
  {
    id: 'floorPlan',
    label: messages.floorplan.defaultMessage,
    sortable: true,
    searchable: true,
    dataType: 'string',
  },
  {
    id: 'resident',
    label: isAllCommercial
      ? messages.tenant.defaultMessage
      : messages.resident.defaultMessage,
    sortable: true,
    searchable: true,
    dataType: 'string',
  },
  {
    id: 'moveInDate',
    label: messages.moveIn.defaultMessage,
    sortable: true,
    searchable: true,
    dataType: 'date',
  },
  {
    id: 'leaseStartDate',
    label: messages.leaseStartDate.defaultMessage,
    sortable: true,
    searchable: true,
    dataType: 'date',
  },
  {
    id: 'leaseEndDate',
    label: messages.leaseEndDate.defaultMessage,
    sortable: true,
    searchable: true,
    dataType: 'date',
  },
  {
    id: 'daysUntilLeaseEnd',
    label: messages.daysUntilLeaseEnd.defaultMessage,
    sortable: true,
    searchable: true,
    dataType: 'number',
  },
  {
    id: 'noticeToVacate',
    label: messages.noticeToVacate.defaultMessage,
    sortable: true,
    searchable: false,
    multiselect: true,
    filterOptions: YES_NO_FILTER_OPTIONS,
    dataType: 'boolean',
  },
  {
    id: 'doNotRenew',
    label: messages.doNotRenew.defaultMessage,
    sortable: true,
    searchable: false,
    multiselect: true,
    filterOptions: YES_NO_FILTER_OPTIONS,
    dataType: 'boolean',
  },
  {
    id: 'underEviction',
    label: messages.underEviction.defaultMessage,
    sortable: true,
    searchable: false,
    multiselect: true,
    filterOptions: YES_NO_FILTER_OPTIONS,
    dataType: 'boolean',
  },
  {
    id: 'marketRent',
    label: messages.gprMarketRent.defaultMessage,
    sortable: true,
    searchable: true,
    dataType: 'number',
  },
  {
    id: 'priorLeasedRent',
    label: messages.priorLeasedRent.defaultMessage,
    sortable: true,
    searchable: true,
    dataType: 'number',
  },
  {
    id: 'currentLeasedRent',
    label: messages.currentLeasedRent.defaultMessage,
    sortable: true,
    searchable: true,
    dataType: 'number',
  },
  {
    id: 'otherUnitFees',
    label: messages.otherUnitFees.defaultMessage,
    sortable: true,
    searchable: true,
    dataType: 'number',
  },
  {
    id: 'balance',
    label: messages.balance.defaultMessage,
    sortable: true,
    searchable: true,
    dataType: 'number',
  },
  {
    id: 'quotingRent',
    label: messages.quotingRent.defaultMessage,
    sortable: true,
    searchable: true,
    dataType: 'number',
  },
  {
    id: 'note',
    label: messages.note.defaultMessage,
    sortable: false,
    searchable: false,
  },
];

// Id-keyed map the library's sort, filter and search read to resolve a
// header id to a field path (on the flattened row) plus its dataType. Every
// `HEADERS` id must have a matching entry here.
export const PROPERTY_PATH_MAP = {
  leaseRenewalStatus: {
    path: 'leaseRenewalStatus',
    dataType: 'string',
  },
  unit: {
    path: 'unit',
    dataType: 'string',
  },
  floorPlan: {
    path: 'floorPlan',
    dataType: 'string',
  },
  resident: {
    path: 'resident',
    dataType: 'string',
  },
  moveInDate: {
    path: 'moveInDate',
    dataType: 'date',
  },
  leaseStartDate: {
    path: 'leaseStartDate',
    dataType: 'date',
  },
  leaseEndDate: {
    path: 'leaseEndDate',
    dataType: 'date',
  },
  daysUntilLeaseEnd: {
    path: 'daysUntilLeaseEnd',
    dataType: 'number',
  },
  noticeToVacate: {
    path: 'noticeToVacate',
    dataType: 'boolean',
  },
  doNotRenew: {
    path: 'doNotRenew',
    dataType: 'boolean',
  },
  underEviction: {
    path: 'underEviction',
    dataType: 'boolean',
  },
  marketRent: {
    path: 'marketRent',
    dataType: 'number',
  },
  priorLeasedRent: {
    path: 'priorLeasedRent',
    dataType: 'number',
  },
  currentLeasedRent: {
    path: 'currentLeasedRent',
    dataType: 'number',
  },
  otherUnitFees: {
    path: 'otherUnitFees',
    dataType: 'number',
  },
  balance: {
    path: 'balance',
    dataType: 'number',
  },
  quotingRent: {
    path: 'quotingRent',
    dataType: 'number',
  },
  note: {
    path: 'notes',
    dataType: 'string',
  },
  actions: {
    path: 'residentId',
    dataType: 'string',
  },
};
